import { clsx } from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatCurrency, formatSignedCurrency } from "../../../../../lib/money";
import LineItemRow from "../../../components/LineItemRow";
import type { DealStatusData } from "../api/types";
import DealFeesModal from "../DealFeesModal";
import type { DifferenceTone } from "../DealStatus.calculations";
import { calculateDifferenceInAmountPaid, getDifferenceTone } from "../DealStatus.calculations";

type LineItemLabelKey =
  | "dealStatus.lineItems.vehicleAdvance"
  | "dealStatus.lineItems.capsAdvantage"
  | "dealStatus.lineItems.vehicleServiceContract"
  | "dealStatus.lineItems.vscNetDealerCost"
  | "dealStatus.lineItems.vscProfit"
  | "dealStatus.lineItems.gap"
  | "dealStatus.lineItems.gapNetDealerCost"
  | "dealStatus.lineItems.gapProfit"
  | "dealStatus.lineItems.financeCompensation"
  | "dealStatus.lineItems.dealFees";

interface SubItem {
  labelKey: LineItemLabelKey;
  amount?: number;
}

interface LineItem {
  labelKey: LineItemLabelKey;
  // Positive amounts add, negative amounts subtract; undefined renders "-".
  amount?: number;
  // Only consulted when `amount` is exactly 0 (see formatSignedCurrency).
  negativeZero?: boolean;
  subItems?: SubItem[];
  isLink?: boolean;
}

const DIFFERENCE_TONE_CLASS: Record<DifferenceTone, string> = {
  positive: "text-(--color-success-strong)",
  negative: "text-destructive",
  neutral: "",
};

const underlineClassName = "border-border border-t border-dashed";

const formatOptionalCurrency = (amount: number | undefined) => (amount === undefined ? "-" : formatCurrency(amount));
const negate = (amount: number | undefined) => (amount === undefined ? undefined : -amount);

interface FinancialBreakdownProperties {
  data: DealStatusData;
}

function FinancialBreakdown({ data }: FinancialBreakdownProperties) {
  const { t } = useTranslation();
  const [isDealFeesModalOpen, setIsDealFeesModalOpen] = useState(false);
  const { dealStatus, flags } = data;
  const today = dealStatus?.todayAmounts;
  const isPurchaseProgram = flags?.purchaseProgram === true;
  const isThirdPartyEligible = flags?.thirdPartyEligible === true;
  const hasTpap = flags?.anyAncillaryVendorTpap === true;

  const dealerFees = dealStatus?.dealerFeeList ?? [];
  const dealerFeesTotal = dealStatus?.totalDealerFees ?? dealerFees.reduce((sum, fee) => sum + (fee.feeAmount ?? 0), 0);
  const dealFeesItem: LineItem = {
    labelKey: "dealStatus.lineItems.dealFees",
    amount: -dealerFeesTotal,
    negativeZero: true,
    isLink: dealerFees.length > 0,
  };

  const capsAdvantageAmount = dealStatus?.capsAggpAdvAmount;
  const financeCompAmount = flags?.dealerFinanceCompAmount;

  const advanceItems: LineItem[] = [
    // "Today" values only — initialFunding / amountPaidToDealer are the at-submit values.
    { labelKey: "dealStatus.lineItems.vehicleAdvance", amount: today?.vehicleAdvance },
    ...(flags?.capsAdvantageEnabled && capsAdvantageAmount !== undefined
      ? [{ labelKey: "dealStatus.lineItems.capsAdvantage" as const, amount: capsAdvantageAmount }]
      : []),
    {
      labelKey: "dealStatus.lineItems.vehicleServiceContract",
      amount: dealStatus?.vscSellingPrice,
      subItems: hasTpap
        ? undefined
        : [
            { labelKey: "dealStatus.lineItems.vscNetDealerCost", amount: dealStatus?.vscDealerCost },
            { labelKey: "dealStatus.lineItems.vscProfit", amount: dealStatus?.vscCommission },
          ],
    },
    {
      labelKey: "dealStatus.lineItems.gap",
      amount: dealStatus?.gapSellingPrice,
      subItems: hasTpap
        ? undefined
        : [
            { labelKey: "dealStatus.lineItems.gapNetDealerCost", amount: dealStatus?.gapDealerCost },
            { labelKey: "dealStatus.lineItems.gapProfit", amount: dealStatus?.gapCommission },
          ],
    },
    ...(flags?.dealerFinanceCompEnabled && financeCompAmount !== undefined
      ? [{ labelKey: "dealStatus.lineItems.financeCompensation" as const, amount: financeCompAmount }]
      : []),
    ...(isPurchaseProgram ? [dealFeesItem] : []),
  ];

  const deductions: LineItem[] = [
    ...(isPurchaseProgram ? [] : [dealFeesItem]),
    ...(hasTpap
      ? []
      : [
          {
            labelKey: "dealStatus.lineItems.vscNetDealerCost" as const,
            amount: negate(dealStatus?.vscDealerCost),
            negativeZero: true,
          },
          {
            labelKey: "dealStatus.lineItems.gapNetDealerCost" as const,
            amount: negate(dealStatus?.gapDealerCost),
            negativeZero: true,
          },
        ]),
  ];

  const totalToday = isThirdPartyEligible || isPurchaseProgram ? today?.dealerFunding : today?.dealerStatementAdvance;

  // Third-party eligibility selects a purchase-price comparison, independently of TPAP.
  const comparisonToday = isThirdPartyEligible ? today?.dealerFunding : today?.amountPaidToDealer;
  const comparisonAtSubmit = isThirdPartyEligible ? dealStatus?.dealerFunding : dealStatus?.amountPaidToDealer;
  const difference =
    comparisonToday !== undefined && comparisonAtSubmit !== undefined
      ? calculateDifferenceInAmountPaid(comparisonToday, comparisonAtSubmit)
      : undefined;

  const renderRow = (item: LineItem) => (
    <LineItemRow
      key={item.labelKey}
      label={
        item.isLink ? (
          <button
            type="button"
            onClick={() => setIsDealFeesModalOpen(true)}
            className="text-primary cursor-pointer text-left underline underline-offset-4"
          >
            {t(item.labelKey)}
          </button>
        ) : (
          t(item.labelKey)
        )
      }
      value={item.amount === undefined ? "-" : formatSignedCurrency(item.amount, item.negativeZero)}
    />
  );

  return (
    <div className="flex flex-col">
      <div className="gap-(--spacing-1) flex flex-col">
        {advanceItems.map((item) =>
          item.subItems ? (
            <div key={item.labelKey}>
              {renderRow(item)}
              {item.subItems.map((subItem) => (
                <LineItemRow
                  key={subItem.labelKey}
                  label={t(subItem.labelKey)}
                  value={formatOptionalCurrency(subItem.amount)}
                  indent
                />
              ))}
            </div>
          ) : (
            renderRow(item)
          )
        )}
      </div>

      <LineItemRow
        label={t(
          isThirdPartyEligible
            ? "dealStatus.purchasePriceToday"
            : isPurchaseProgram
              ? "dealStatus.purchasePrice"
              : "dealStatus.advance"
        )}
        value={formatOptionalCurrency(totalToday)}
        className={clsx("font-(--font-weight-bold) mt-(--spacing-2) pt-(--spacing-2)", underlineClassName)}
        valueClassName="text-primary"
      />

      {!isThirdPartyEligible && deductions.length > 0 && (
        <div className="gap-(--spacing-1) mt-(--spacing-1) flex flex-col">
          {deductions.map((item) => renderRow(item))}
        </div>
      )}

      {!isThirdPartyEligible && (
        <LineItemRow
          label={t("dealStatus.amountPaidToDealerToday")}
          value={formatOptionalCurrency(comparisonToday)}
          className={clsx("font-(--font-weight-bold) mt-(--spacing-2) pt-(--spacing-2)", underlineClassName)}
          valueClassName="text-primary"
        />
      )}

      <div className={clsx("mt-(--spacing-2) pt-(--spacing-2) gap-(--spacing-1) flex flex-col", underlineClassName)}>
        <LineItemRow
          label={t(isThirdPartyEligible ? "dealStatus.purchasePriceAtSubmit" : "dealStatus.amountExpectedAtSubmit")}
          value={formatOptionalCurrency(comparisonAtSubmit)}
        />
        <LineItemRow
          label={t(isThirdPartyEligible ? "dealStatus.differenceInPurchasePrice" : "dealStatus.differenceInAmountPaid")}
          value={difference === undefined ? "-" : formatCurrency(difference)}
          valueClassName={difference === undefined ? undefined : DIFFERENCE_TONE_CLASS[getDifferenceTone(difference)]}
        />
      </div>

      <DealFeesModal
        open={isDealFeesModalOpen}
        onOpenChange={setIsDealFeesModalOpen}
        fees={dealerFees}
        total={dealerFeesTotal}
      />
    </div>
  );
}

export default FinancialBreakdown;
